// Personas — hub do fluxo core: Persona → Locais → Zonas → Campanha → Performance.
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const EMPTY = {
  name: "", age_range: "", estimated_income: "", interests: "",
  commercial_goal: "", target_region: "", radius_km: "", average_ticket: "",
};

export default function Personas() {
  const [personas, setPersonas] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);   // selected persona id
  const [places, setPlaces] = useState([]);
  const [zones, setZones] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = async () => setPersonas(await api.listPersonas());
  useEffect(() => { load().catch((e) => setErr(String(e.message))); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setErr("");
    try {
      const body = {
        ...form,
        interests: form.interests ? form.interests.split(",").map((s) => s.trim()) : [],
        radius_km: form.radius_km ? Number(form.radius_km) : null,
        average_ticket: form.average_ticket ? Number(form.average_ticket) : null,
      };
      await api.createPersona(body);
      setForm(EMPTY); await load();
    } catch (e) { setErr(String(e.message)); } finally { setCreating(false); }
  };

  const open = async (id) => {
    setActive(id); setPlaces([]); setZones([]); setCampaigns([]); setErr("");
    try {
      setPlaces(await api.listPlaces(id));
      setZones(await api.listZones(id));
      setCampaigns(await api.listCampaigns(id));
    } catch (e) { setErr(String(e.message)); }
  };

  const run = async (label, fn, set) => {
    setBusy(label); setErr("");
    try { const r = await fn(); if (set) set(r); }
    catch (e) { setErr(String(e.message)); } finally { setBusy(""); }
  };

  const card = "rounded-lg border border-neutral-800 bg-neutral-900/50 p-4";
  const inp = "w-full rounded-md bg-neutral-800 border border-neutral-700 px-3 py-2 text-sm";
  const btn = "rounded-md bg-red-600 hover:bg-red-500 px-4 py-2 text-sm font-medium disabled:opacity-50";

  return (
    <Layout>
      <h1 className="text-2xl font-bold">Personas</h1>
      <p className="text-neutral-400 text-sm mt-1">
        Defina quem você quer adquirir. O Tomahawk descobre onde essa persona
        circula no mundo real e transforma esses lugares em aquisição previsível.
      </p>
      {err && <div className="mt-4 rounded-md bg-red-600/15 text-red-400 px-4 py-2 text-sm">{err}</div>}

      {/* Etapa 1 — criar persona */}
      <form onSubmit={create} className={`${card} mt-6 grid grid-cols-2 gap-3`}>
        <div className="col-span-2 text-xs uppercase tracking-wide text-neutral-500">Etapa 1 · Definir Persona</div>
        <input className={inp} placeholder="Nome da persona" value={form.name} required
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className={inp} placeholder="Faixa etária (ex: 30-45)" value={form.age_range}
          onChange={(e) => setForm({ ...form, age_range: e.target.value })} />
        <input className={inp} placeholder="Renda estimada" value={form.estimated_income}
          onChange={(e) => setForm({ ...form, estimated_income: e.target.value })} />
        <input className={inp} placeholder="Interesses (vírgula)" value={form.interests}
          onChange={(e) => setForm({ ...form, interests: e.target.value })} />
        <input className={inp} placeholder="Objetivo comercial" value={form.commercial_goal}
          onChange={(e) => setForm({ ...form, commercial_goal: e.target.value })} />
        <input className={inp} placeholder="Região-alvo (ex: Jardins, São Paulo)" value={form.target_region}
          onChange={(e) => setForm({ ...form, target_region: e.target.value })} />
        <input className={inp} placeholder="Raio (km)" value={form.radius_km}
          onChange={(e) => setForm({ ...form, radius_km: e.target.value })} />
        <input className={inp} placeholder="Ticket médio (R$)" value={form.average_ticket}
          onChange={(e) => setForm({ ...form, average_ticket: e.target.value })} />
        <div className="col-span-2"><button className={btn} disabled={creating}>{creating ? "Criando…" : "Criar persona"}</button></div>
      </form>

      {/* Lista */}
      <div className="mt-6 space-y-2">
        {personas.map((p) => (
          <div key={p.id} className={card}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-neutral-500">
                  {p.target_region || "sem região"} · {(p.interests || []).join(", ")}
                </div>
              </div>
              <button className="text-sm text-red-400 hover:text-red-300"
                onClick={() => open(active === p.id ? null : p.id)}>
                {active === p.id ? "Fechar" : "Abrir fluxo →"}
              </button>
            </div>

            {active === p.id && (
              <div className="mt-4 border-t border-neutral-800 pt-4 space-y-4">
                {/* Etapa 2 */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-neutral-500">Etapa 2 · Locais ({places.length})</span>
                    <button className={btn} disabled={busy}
                      onClick={() => run("places", () => api.generatePlaces(p.id), setPlaces)}>
                      {busy === "places" ? "Buscando…" : "Identificar locais"}
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {places.slice(0, 8).map((pl) => (
                      <div key={pl.id} className="text-xs bg-neutral-800/50 rounded px-2 py-1">
                        <span className="font-medium">{pl.name}</span> · af {pl.affinity_score} / int {pl.intent_score}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Etapa 3 */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-neutral-500">Etapa 3 · Zonas de Intenção ({zones.length})</span>
                    <button className={btn} disabled={busy || !places.length}
                      onClick={() => run("zones", () => api.generateZones(p.id), setZones)}>
                      {busy === "zones" ? "Clusterizando…" : "Gerar zonas"}
                    </button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {zones.map((z) => (
                      <div key={z.id} className="bg-neutral-800/50 rounded px-3 py-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{z.name}</span>
                          <span className="text-xs text-neutral-400">opp {z.opportunity_score} · {z.priority}</span>
                        </div>
                        <button className="mt-1 text-xs text-red-400 hover:text-red-300" disabled={busy}
                          onClick={() => run("camp", async () => {
                            await api.generateCampaign(z.id);
                            return api.listCampaigns(p.id);
                          }, setCampaigns)}>
                          {busy === "camp" ? "Gerando…" : "Etapa 4 · Ativar campanha →"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Etapa 4/5 */}
                {campaigns.length > 0 && (
                  <div>
                    <span className="text-xs uppercase tracking-wide text-neutral-500">Campanhas ({campaigns.length})</span>
                    <div className="mt-2 space-y-2">
                      {campaigns.map((c) => (
                        <div key={c.id} className="bg-neutral-800/50 rounded px-3 py-2 text-sm">
                          <div className="font-medium">{c.objective?.slice(0, 80)}</div>
                          <div className="text-xs text-neutral-400 mt-1">
                            {c.channel?.slice(0, 60)} · R${c.suggested_budget}/dia · {c.suggested_duration_days}d · {c.status}
                          </div>
                          <button className="mt-1 text-xs text-red-400 hover:text-red-300" disabled={busy}
                            onClick={() => run("perf", () => api.recordPerformance(c.id, {}), null)}>
                            {busy === "perf" ? "Calculando…" : "Etapa 5 · Projetar performance →"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!personas.length && <div className="text-neutral-500 text-sm">Nenhuma persona ainda. Crie a primeira acima.</div>}
      </div>
    </Layout>
  );
}
